import ConfirmModal from '@/components/confirm-modal'
import { AccessibleCompany } from '@/lib/api/common/interfaces/user.interface'
import {
    deleteCompany,
    getAccessibleCompanies,
} from '@/lib/api/organization/organization-api'
import { canHaveBranches } from '@/lib/person-type'
import { useAppSelector } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdApartment,
    MdBusiness,
    MdDelete,
    MdEdit,
    MdPeople,
    MdRefresh,
    MdSearch,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

export default function UnitsPage() {
    const navigate = useNavigate()
    const { user, accessibleCompanies: storeCompanies } = useAppSelector(
        state => state.auth
    )
    const [companies, setCompanies] = useState<AccessibleCompany[]>([])
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState(false)
    const [companyToDelete, setCompanyToDelete] =
        useState<AccessibleCompany | null>(null)
    const [searchTerm, setSearchTerm] = useState('')

    useEffect(() => {
        loadCompanies()
    }, [])

    const loadCompanies = async () => {
        setLoading(true)
        try {
            const response = await getAccessibleCompanies()
            if (response.success && response.result) {
                setCompanies(response.result)
            } else {
                // Fallback para store
                setCompanies(storeCompanies)
            }
        } catch (error) {
            console.error('Erro ao carregar unidades:', error)
            setCompanies(storeCompanies)
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!companyToDelete) return

        setDeleting(true)
        try {
            const response = await deleteCompany(companyToDelete.id)
            if (response.success) {
                toast.success(
                    `Unidade "${companyToDelete.tradingName || companyToDelete.name}" excluída com sucesso!`
                )
                setCompanyToDelete(null)
                loadCompanies()
            } else {
                toast.error(response.error || 'Erro ao excluir unidade')
            }
        } catch (error: any) {
            toast.error(error?.message || 'Erro ao excluir unidade')
        } finally {
            setDeleting(false)
        }
    }

    const filteredCompanies = companies.filter(
        company =>
            company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (company.tradingName &&
                company.tradingName
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()))
    )

    const currentCompany = companies.find(c => c.id === user?.companyId)

    // Pessoa física não tem filial: quem é PF é a própria pessoa, e uma segunda
    // unidade com o mesmo CPF não existe juridicamente. O backend também recusa.
    const canCreateBranch = canHaveBranches(user?.company)

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-base-content flex items-center gap-2">
                        <MdApartment className="text-secondary" />
                        Unidades
                    </h1>
                    <p className="text-sm text-base-content/60 mt-1">
                        {canCreateBranch
                            ? 'Gerencie as unidades/filiais'
                            : 'Conta de pessoa física possui uma única unidade — filiais exigem CNPJ.'}
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={loadCompanies}
                        disabled={loading}
                    >
                        <MdRefresh
                            className={`text-lg ${loading ? 'animate-spin' : ''}`}
                        />
                    </button>
                    {canCreateBranch && (
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={() => navigate('/person/companies/new')}
                        >
                            <MdAdd className="text-lg" />
                            Nova Unidade
                        </button>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="stat bg-base-100 rounded-box shadow-sm border border-base-300">
                    <div className="stat-figure text-secondary">
                        <MdApartment className="text-3xl" />
                    </div>
                    <div className="stat-title">Total de Unidades</div>
                    <div className="stat-value text-secondary">
                        {companies.length}
                    </div>
                </div>
                <div className="stat bg-base-100 rounded-box shadow-sm border border-base-300">
                    <div className="stat-figure text-success">
                        <MdBusiness className="text-3xl" />
                    </div>
                    <div className="stat-title">Unidade Atual</div>
                    <div className="stat-value text-success text-lg truncate">
                        {currentCompany?.tradingName ||
                            currentCompany?.name ||
                            '-'}
                    </div>
                </div>
                <div className="stat bg-base-100 rounded-box shadow-sm border border-base-300">
                    <div className="stat-figure text-info">
                        <MdPeople className="text-3xl" />
                    </div>
                    <div className="stat-title">Seu Acesso</div>
                    <div className="stat-value text-info">
                        {companies.length}{' '}
                        {companies.length === 1 ? 'unidade' : 'unidades'}
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="mb-4">
                <div className="relative max-w-md">
                    <input
                        type="text"
                        placeholder="Buscar unidade..."
                        className="input input-bordered w-full pl-10"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                    <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/50 text-lg" />
                </div>
            </div>

            {/* Lista */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            ) : filteredCompanies.length === 0 ? (
                <div className="text-center py-12 bg-base-100 rounded-box border border-base-300">
                    <MdApartment className="text-5xl text-base-content/30 mx-auto mb-4" />
                    <p className="text-base-content/60">
                        {searchTerm
                            ? 'Nenhuma unidade encontrada para esta busca.'
                            : 'Nenhuma unidade cadastrada ainda.'}
                    </p>
                    {!searchTerm && canCreateBranch && (
                        <button
                            className="btn btn-secondary btn-sm mt-4"
                            onClick={() => navigate('/person/companies/new')}
                        >
                            <MdAdd /> Criar primeira unidade
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredCompanies.map(company => (
                        <div
                            key={company.id}
                            className={`card bg-base-100 shadow-sm border hover:shadow-md transition-shadow ${
                                company.id === user?.companyId
                                    ? 'border-secondary/50 bg-secondary/5'
                                    : 'border-base-300'
                            }`}
                        >
                            <div className="card-body p-4">
                                <div className="flex items-start gap-3">
                                    <div className={`avatar placeholder`}>
                                        <div
                                            className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                                company.id === user?.companyId
                                                    ? 'bg-secondary text-primary-content'
                                                    : 'bg-base-300 text-base-content'
                                            }`}
                                        >
                                            <span className="text-xl font-bold">
                                                {company.name
                                                    .charAt(0)
                                                    .toUpperCase()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-base-content truncate">
                                            {company.tradingName ||
                                                company.name}
                                        </h3>
                                        {company.tradingName &&
                                            company.name !==
                                                company.tradingName && (
                                                <p className="text-xs text-base-content/60 truncate">
                                                    {company.name}
                                                </p>
                                            )}
                                        {company.id === user?.companyId && (
                                            <span className="badge badge-secondary badge-xs mt-1">
                                                Atual
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="card-actions justify-end mt-3 pt-3 border-t border-base-200">
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        onClick={() =>
                                            navigate(
                                                `/person/companies/${company.id}`
                                            )
                                        }
                                    >
                                        <MdEdit className="text-base" />
                                        Editar
                                    </button>
                                    {company.id !== user?.companyId && (
                                        <button
                                            className="btn btn-ghost btn-sm text-error"
                                            onClick={() =>
                                                setCompanyToDelete(company)
                                            }
                                        >
                                            <MdDelete className="text-base" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={!!companyToDelete}
                onClose={() => setCompanyToDelete(null)}
                onConfirm={handleDelete}
                title="Excluir Unidade"
                message={`Tem certeza que deseja excluir a unidade "${companyToDelete?.tradingName || companyToDelete?.name}"? Esta ação não poderá ser desfeita. Todos os dados associados a esta unidade serão desativados.`}
                loading={deleting}
                confirmButtonText="Excluir"
            />
        </div>
    )
}
